/**
 * Desktop - Cart items molecule.
 */

/* global OCMS */

// Imports
import NanoscrollerManager from '../../common/tools/NanoscrollerManager';
import {
  cartUpdateEventName,
  registerCallback as registerEventCallback,
} from '../../common/Events';
import {
  isEmpty,
  value,
} from '../../common/Utils';
import {
  hasItems,
  updateSkusList as cartUpdateSkusList,
  removeItem,
} from '../Cart';
import cartQuickLinkId from '../../common/molecule/cart-items';
import {
  closeNotifications,
} from '../organism/reward-notifications';

// Constants (ids)
const cartItemsAreaId = 'cartItemsArea';
const cartItemsListId = 'cartItemsList';

// Constants (classes)
const cartItemsMoleculeClass = 'm-cart-items';
const cartItemsAreaClass = `${cartItemsMoleculeClass}__items-area`;
const cartListItemClass = `${cartItemsMoleculeClass}__item`;
const cartRemoveItemButtonClass = `${cartItemsMoleculeClass}__close-btn`;
const cartItemTitleClass = 'm-cart-item-thumbnail__text';
const cartItemPriceClass = `${cartItemsMoleculeClass}__price`;

// Constants (attributes)
const dataIdAttr = 'data-id';
const dataSkuAttr = 'data-sku';
const dataCategoryAttr = 'data-category';
const dataBrandAttr = 'data-brand';
const dataQuantityAttr = 'data-quantity';
const dataVariantAttr = 'data-variant';
const dataProductClasstAttr = 'data-class';

// Constants (selectors)
const cartItemsAreaSelector = `#${cartItemsAreaId}`;
const cartItemsListSelector = `#${cartItemsListId}`;
const cartItemsListItemSelector = '.m-cart-items__item';

// Constants (functional)
const maxItemsListHeight = 300;

/**
 * Reference to the cart "quick link" element.
 * @type {Object}
 */
let cartQuickLink = {};

/**
 * Reference to the cart items area element.
 * @type {Object}
 */
let cartItemsArea = {};

/**
 * Indicator for whether or not the cart items area is visible.
 * @type {Boolean}
 */
let cartItemsAreaVisible = false;

/**
 * Reference to the close animation timeout.
 * @type {Object}
 */
let closingTimeout = null;

/**
 * Indicator for whether or not a fade animation is occurring.
 * @type {Boolean}
 */
let fadeAnimationOccurring = false;

/**
 * Reference to the nanoscroller manager.
 * @type {Object}
 */
let nanoscrollerManager = null;

/**
 * Reference to the cart items list element.
 * @type {Object}
 */
let cartItemsList = {};

function saveReferences() {
  // Save reference to the cart "quick link" element
  cartQuickLink = $(`#${cartQuickLinkId}`);

  // Save reference to the cart items area element
  cartItemsArea = $(`#${cartItemsAreaId}`);

  // Save reference to the cart items list element
  cartItemsList = $(`#${cartItemsListId}`);
}

function updateSkusList() {
  const skusList = [];
  const $cartItemsList = $(cartItemsListSelector);
  const $items = $cartItemsList.find(cartItemsListItemSelector);

  $items.each((anIndex, aoElement) => {
    const sku = $(aoElement).attr(dataSkuAttr);

    if (!isEmpty(sku)) {
      skusList.push(sku);
    }
  });

  cartUpdateSkusList(skusList);
}

function checkNanoscroller(asSKU) {
  const getCartItemsArea = () => cartItemsArea.find(`.${cartItemsAreaClass}`);

  const $cartItemsArea = getCartItemsArea();

  if (nanoscrollerManager === null) {
    nanoscrollerManager = new NanoscrollerManager($cartItemsArea, getCartItemsArea);
  }

  setTimeout(() => {
    // Get content height
    const height = nanoscrollerManager.getContentHeight();

    if (height < maxItemsListHeight) {
      nanoscrollerManager.inactivate(height);
    } else {
      nanoscrollerManager.activate(
        maxItemsListHeight,
        (asSKU) ? cartItemsList.find(`.${cartListItemClass}[data-sku="${asSKU}"]`) : null
      );
    }
  }, 0);
}

function openCart() {
  if (
    !hasItems() ||
    ((typeof OCMS !== 'undefined') && OCMS.isCheckout && OCMS.isCheckout())
  ) {
    return;
  }

  if (!fadeAnimationOccurring && !cartItemsAreaVisible) {
    fadeAnimationOccurring = true;

    // Show the cart items
    cartItemsArea.fadeIn({
      duration: 'fast',
      complete: () => {
        fadeAnimationOccurring = false;

        cartItemsAreaVisible = true;
      },
    });

    // Check the nanoscroller
    checkNanoscroller();
  }
}

function animateCartClosing() {
  fadeAnimationOccurring = true;

  cartItemsArea.fadeOut({
    duration: 'fast',
    complete: () => {
      fadeAnimationOccurring = false;

      cartItemsAreaVisible = false;
    },
  });
}

function closeCart(anDelay) {
  if (typeof OCMS !== 'undefined' && OCMS.isCheckout && OCMS.isCheckout()) {
    return;
  }

  if (!fadeAnimationOccurring && cartItemsAreaVisible) {
    // Hide the cart items
    if (anDelay >= 0) {
      closingTimeout = setTimeout(animateCartClosing, anDelay);
    } else {
      animateCartClosing();
    }
  }
}

function handleCartUpdate(aoEvent, abSuccess, aoRootElement) {
  if (!abSuccess) {
    return;
  }

  // Update the cart items area
  if (isEmpty(aoRootElement) || !$(aoRootElement).length) {
    // Empty the items area
    cartItemsArea.empty();

    // Close the items area
    closeCart(400);
  } else {
    let $container = $(aoRootElement).find(cartItemsAreaSelector);
    if (!$container.length) {
      $container = $(aoRootElement);
    }

    cartItemsArea.html($container.html());
  }

  // Update the skus list
  updateSkusList();

  // Check the nanoscroller
  checkNanoscroller();
}

function handleCartItemRemove(aoEvent) {
  const $target = $(aoEvent.currentTarget);
  const $item = $target.parents(`[${dataSkuAttr}]`);

  if (!$item.length) {
    return false;
  }

  // Remove item
  $item.remove();

  // Prevent closing
  clearTimeout(closingTimeout);

  // Set focus on area container
  cartItemsArea.focus();

  const productId = $item.attr(dataIdAttr);
  const sku = $item.attr(dataSkuAttr);
  const quantity = $item.attr(dataQuantityAttr);
  const category = $item.attr(dataCategoryAttr);
  const brand = $item.attr(dataBrandAttr);
  const variant = $item.attr(dataVariantAttr);
  const productClass = $item.attr(dataProductClasstAttr);

  if (isEmpty(sku)) {
    return false;
  }

  const titleContainer = $item.find(`.${cartItemTitleClass}`);
  const priceContainer = $item.find(`.${cartItemPriceClass}`);

  return removeItem(
    productId,
    sku,
    value(titleContainer.text(), ''),
    value(priceContainer.data('price'), ''),
    quantity,
    category,
    brand,
    variant,
    productClass
  );
}

function setEventHandlers() {
  if (cartQuickLink.length) {
    registerEventCallback({
      target: cartQuickLink,
      events: 'mouseover focusin',
      handler: function onCartQuickLinkMouseover() {
        clearTimeout(closingTimeout);

        // Show the cart items
        closeNotifications();
        openCart();
      },
    });

    registerEventCallback({
      target: cartQuickLink,
      events: 'mouseout focusout',
      handler: function onCartQuickLinkMouseout(aoEvent) {
        clearTimeout(closingTimeout);

        // Hide the cart items
        closeCart((aoEvent.type === 'focusout') ? 100 : 1000);
      },
    });
  }

  if (cartItemsArea.length) {
    registerEventCallback({
      target: cartItemsArea,
      events: 'mouseover focusin',
      handler: function onCartItemsAreaMouseover() {
        clearTimeout(closingTimeout);
      },
    });

    registerEventCallback({
      target: cartItemsArea,
      events: 'mousemove',
      handler: function onCartItemsAreaMousemove() {
        clearTimeout(closingTimeout);
      },
    });

    registerEventCallback({
      target: cartItemsArea,
      events: 'mouseout focusout',
      handler: function onCartItemsAreaMouseout() {
        // Hide the cart items
        closeCart(400);
      },
    });

    // Register callback for cart update
    registerEventCallback({
      target: document,
      events: cartUpdateEventName,
      handler: handleCartUpdate,
    });

    // Register callback for cart item removal
    registerEventCallback({
      target: `#${cartItemsAreaId} .${cartRemoveItemButtonClass}`,
      delegate: true,
      events: 'click',
      handler: handleCartItemRemove,
    });
  }
}

function init() {
  // Save references to DOM elements
  saveReferences();

  // Set event handlers
  setEventHandlers();

  // Update the skus list
  updateSkusList();
}

// Named exports - Constants
export {
  cartItemsAreaId,
  cartItemsListId,
};

// Named exports - Functions
export {
  init,
  openCart,
  closeCart,
};



// WEBPACK FOOTER //
// ./js/desktop/molecule/cart-items.js